import dayjs from 'dayjs'
import { useMemo } from 'react'

import {
  isWellReadObservation,
  WellPage_WellCulture,
} from '~/api/backend/pages/wellPageAPI'
import LineGraph from '~/components/LineGraph'
import { generateWellColor } from '~/pages/Monitor/utils/MonitorGraphColors'

export interface WellCellConcentrationGraphProps {
  wellCulture: WellPage_WellCulture
  onSelectObservation?: (timestamp: number) => void
  onClearSelection?: () => void
  // When provided, the graph will show and persist the tooltip for this timestamp (unix seconds)
  selectedTimestamp?: number
}

interface CellConcentrationObservation {
  timestamp: number // Unix timestamp in seconds
  concentration: number // cells per mL
}

export function WellCellConcentrationGraph({
  wellCulture,
  onSelectObservation,
  onClearSelection,
  selectedTimestamp,
}: WellCellConcentrationGraphProps) {
  const { graphData, legendData, allTimestamps, minTime, maxTime } = useMemo(() => {
    const wellConcentrationData: CellConcentrationObservation[] = []
    const wellColor = generateWellColor(0)

    // Extract cell concentration data for the specified well only
    wellCulture.observationHistory
      .filter(isWellReadObservation)
      .forEach(observation => {
        const timestamp = dayjs(observation.timestamp).unix()
        if (observation.cell_concentration != null) {
          wellConcentrationData.push({
            timestamp,
            concentration: observation.cell_concentration,
          })
        }
      })

    // Sort data by timestamp
    wellConcentrationData.sort((a, b) => a.timestamp - b.timestamp)

    // Get all timestamps for domain calculation
    const allTimestamps = wellConcentrationData.map(obs => obs.timestamp).sort()
    const minTime = allTimestamps[0] ?? Date.now() / 1000
    const maxTime = allTimestamps[allTimestamps.length - 1] ?? Date.now() / 1000

    // Create legend data with latest value
    const legendData = [
      {
        well: wellCulture.well,
        color: wellColor,
        latestValue:
          wellConcentrationData[wellConcentrationData.length - 1]?.concentration ?? 0,
      },
    ]

    // Transform data to match D3LineGraphData format: { [key: string]: number }[]
    const transformedGraphData: { [well: string]: { [key: string]: number }[] } = {
      [wellCulture.well]: wellConcentrationData.map(obs => ({
        timestamp: obs.timestamp,
        concentration: obs.concentration,
      })),
    }

    return {
      graphData: transformedGraphData,
      legendData,
      allTimestamps,
      minTime,
      maxTime,
    }
  }, [wellCulture.observationHistory, wellCulture.well])

  // Return null if no data is available
  if (
    Object.keys(graphData).length === 0 ||
    graphData[wellCulture.well]?.length === 0
  ) {
    return null
  }

  // Calculate dynamic Y-axis domain based on cell concentration values
  const allConcentrationValues = Object.values(graphData).flatMap(data =>
    data.map(obs => obs['concentration']),
  )
  const minConcentration = Math.min(...allConcentrationValues)
  const maxConcentration = Math.max(...allConcentrationValues)

  // Add 10% buffer to the Y-axis range
  const range = maxConcentration - minConcentration
  const buffer = range * 0.1
  const yDomain: [number, number] = [
    Math.max(0, minConcentration - buffer),
    maxConcentration + buffer,
  ]

  // An empty region on either side of the X domain keeps the line from overlapping the Y axis labels.
  const xAxisBuffer = ((maxTime - minTime) * 0.19) / 2
  const xDomain: [number, number] = [minTime - xAxisBuffer, maxTime + xAxisBuffer]
  const allDataOnSameDay =
    dayjs.unix(maxTime).format('YYMMDD') === dayjs.unix(minTime).format('YYMMDD')

  // Using native D3 points rendered by D3LineGraph; no overlay state required.

  return (
    <aside className='border-t-2 border-grey-light py-4 w-full h-full'>
      <h2 className='text-sm font-medium text-grey-darkest mb-4.5'>
        Cell Concentration Over Time
      </h2>

      <LineGraph
        className='h-[700px] w-full'
        layoutOptions={{
          marginTop: 0,
          marginRight: 0,
          marginLeft: 50,
        }}
        axisOptions={{
          xAxisBorderClassName: 'stroke-grey-medium',
          xTickValues:
            allTimestamps.length === 1
              ? [minTime]
              : [minTime, minTime + (maxTime - minTime) / 2, maxTime],
          xTickFormat:
            allDataOnSameDay && allTimestamps.length > 1
              ? (unix: number) => dayjs.unix(unix).format('h:mm A')
              : (unix: number) => dayjs.unix(unix).format('MMM D'),
          xTickSize: 0,
          xTickLabelClassName: 'text-xs font-bold translate-y-1',

          yAxisBorderClassName: 'hidden',
          yTickValues: (() => {
            const range = maxConcentration - minConcentration
            const step = range / 4
            return [
              0,
              minConcentration + step,
              minConcentration + 2 * step,
              minConcentration + 3 * step,
              maxConcentration,
            ]
          })(),
          yTickFormat: (val: number) => val.toExponential(2),
          yTickLineClassName: 'stroke-grey-lightest stroke-1 w-full',
          yTickLabelClassName:
            'text-xs font-bold text-start translate-x-0.5 translate-y-2.25 w-full',
          yTickSize: -5000,
        }}
        options={{
          dotSize: 3, // use native D3 points
          xDomain,
          xKey: 'timestamp',
          yDomain,
          yKey: 'concentration',
          lineColor: (key: string) => {
            const wellData = legendData.find(item => item.well === key)
            return wellData?.color ?? '#666'
          },
        }}
        chartTooltipTriggerFn='onPointHover'
        tooltipPosition='center'
        renderTooltipContents={tp => (
          <div className='px-3 py-2 rounded-md bg-white shadow-lg border border-grey-light text-xs flex flex-col gap-2'>
            <div className='font-bold'>
              {tp.timestamp != null
                ? dayjs.unix(tp.timestamp).format('MMM D, h:mm A')
                : ''}
            </div>
            <div className='text-grey-dark'>
              {tp.value != null
                ? `Concentration: ${Number(tp.value).toExponential(2)} cells/mL`
                : ''}
            </div>
          </div>
        )}
        // Wire click events to update selected observation in parent
        onChartClick={(params: { timestamp?: number } | null) => {
          if (params?.timestamp) {
            onSelectObservation?.(params.timestamp)
          } else {
            onClearSelection?.()
          }
        }}
        selectedTimestamp={selectedTimestamp}
        data={graphData}
      />
    </aside>
  )
}
